import { createBrowserClient } from "@supabase/ssr"

import { captureClientException } from "@/shared/lib/monitoring/capture-client"

import { createReportingFetch } from "./reporting-fetch"
import { createOpaqueKeySafeFetch } from "./opaque-key-fetch"
import { getSupabasePublishableKey } from "./keys"

/**
 * Browser-side Supabase client.
 * Use this in Client Components ("use client").
 * Reads URL/key from env vars — change the URL in .env.local, not here.
 *
 * All non-2xx responses from Supabase endpoints are reported to PostHog via
 * `createReportingFetch`. The Supabase SDK surfaces these as `{ data, error }`
 * rather than throwing, so without this wrapper 403/404/406/5xx errors would
 * never reach our error tracking.
 */
export function createClient() {
  const publishableKey = getSupabasePublishableKey()
  const client = createBrowserClient(
    process.env.NEXT_PUBLIC_SUPABASE_URL!,
    publishableKey,
    {
      cookieOptions: { name: "auth-token" },
      global: {
        fetch: createOpaqueKeySafeFetch(publishableKey, createReportingFetch(
          (err, ctx) => captureClientException(err, ctx),
          "supabase-browser"
        )),
      },
    }
  )
  ;(
    globalThis as typeof globalThis & {
      __pleasuraiSupabaseClient?: typeof client
    }
  ).__pleasuraiSupabaseClient = client
  return client
}
